import React from 'react';
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { Platform } from 'react-native';
import { ScrollView as GestureScrollView } from 'react-native-gesture-handler';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { MAX_CITY_LENGTH, MAX_NAME_LENGTH, MAX_ZIP_LENGTH } from '@/validation';

import { CountryRegionField, StateProvinceField } from '@/components/address-fields';
import { Stack } from '@/components/layout';
import { Checkbox, DateTimePicker, FormInput, FormSelectInput, Text } from '@/components/ui';
import type { FormInputProps } from '@/components/ui/form/form-input';
import type { FormSelectInputProps } from '@/components/ui/form/form-select-input';
import { STRINGS } from '@/constants/strings';
import {
  DOMINANT_HAND_OPTIONS,
  GENDER_OPTIONS,
  HEIGHT_OPTIONS,
  type AddPlayerForm,
  type DominantHand,
  type PlayerGender,
} from '@/features/onboarding/schemas/add-player-schema';
import { useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import { OnboardingAvatar } from '../onboarding-avatar';
import { DeletePlayerButton } from './delete-player-button';
import { FieldError, FieldLabel } from './field-label';
import { useStyles } from './styles';

const strings = STRINGS.onboarding.addPlayer;

type PlayerFormBodyProps = {
  control: Control<AddPlayerForm>;
  /** Clears the dependent State field when the Country changes. */
  setValue: UseFormSetValue<AddPlayerForm>;
  /** Read-only view mode: every field disabled, no delete/save. */
  isViewing: boolean;
  /** Edit mode (an existing queued player) — controls the inline delete action. */
  isEditing: boolean;
  isTablet: boolean;
  isWindowed: boolean;
  onPickAvatar: () => void;
  onToggleSameAddress: (checked: boolean) => void;
  /** Drops the queued player (rendered inline on tablet; the phone sheet renders its own). */
  onDelete: () => void;
};

/**
 * The scrollable form body, identical for phone, landscape sheet, and tablet popup —
 * only the surrounding chrome (header / footer) differs per layout. Owns nothing but the
 * form fields; all state and submit logic live in the parent sheet.
 */
export function PlayerFormBody({
  control,
  setValue,
  isViewing,
  isEditing,
  isTablet,
  isWindowed,
  onPickAvatar,
  onToggleSameAddress,
  onDelete,
}: PlayerFormBodyProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  // Disabled fields reuse the Level field's look (grey fill), so view mode reads consistently.
  // `opacity: 1` overrides the 0.6 fade the Select/DateTimePicker triggers apply themselves when
  // disabled — without it their text reads noticeably lighter than FormInput (which doesn't fade),
  // so we keep every field at full opacity for crisp, readable values in view mode.
  const disabledFieldStyle = { backgroundColor: guardianColors[1], opacity: 1 };

  // Same field styling as onboarding step 1 (label not uppercased, 8px radius, 48px height).
  const sharedFieldProps = {
    uppercaseLabel: false,
    labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
    inputVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    labelGap: scale(4),
    labelStyle: styles.fieldLabel,
    inputStyle: styles.fieldText,
    contentStyle: styles.fieldContent,
  } satisfies Partial<FormInputProps>;

  const selectFieldProps = {
    uppercaseLabel: false,
    labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
    labelGap: scale(4),
    labelStyle: styles.fieldLabel,
    triggerVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    triggerStyle: styles.fieldContent,
  } satisfies Partial<FormSelectInputProps>;

  // State/Province can render as a free-text field (country with no state list), so it also
  // needs the FormInput content/text styling on top of the shared select styling.
  const stateFieldProps = {
    ...selectFieldProps,
    inputVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    inputStyle: styles.fieldText,
    contentStyle: styles.fieldContent,
  } as const;

  // When "Same address with me" is on, the player reuses the guardian's address,
  // so the address fields are hidden (the values are kept in sync on the form).
  const sameAddress = useWatch({ control, name: 'sameAddress' });

  // Country holds the ISO code on the form (mapped to the country name when sent to the API).
  // It starts unselected and drives the State/Province options.
  const selectedCountry = useWatch({ control, name: 'country' });

  // Height + Dominant hand only show for an existing player (edit / view). The add flow keeps
  // the form short per the design; the fields still exist on the schema with their defaults.
  const showHeightAndHand = isEditing || isViewing;
  // "Same address with me" is an add-only convenience (copy the guardian's address). Edit and
  // view work against the player's own address, so the checkbox is hidden there.
  const showSameAddress = !isViewing && !isEditing;
  // Address fields hide only when the add flow reuses the guardian's address; always shown otherwise.
  const showAddressFields = isViewing || isEditing || !sameAddress;

  // On the Android sheet, the default RN ScrollView doesn't yield to SheetContent's
  // drag-to-close Pan gesture, so the form won't scroll. Render through
  // gesture-handler's ScrollView there (it negotiates with the Pan) — SheetContent
  // provides the GestureHandlerRootView it needs. The tablet popup uses the plain
  // Modal (no Pan, no root view), so it keeps the default ScrollView.
  const formScrollComponent =
    !isTablet && Platform.OS === 'android' ? GestureScrollView : undefined;

  return (
    <KeyboardAwareScrollView
      ScrollViewComponent={formScrollComponent}
      enabled={!isWindowed}
      style={styles.keyboardAvoidingView}
      contentContainerStyle={styles.body}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
      bottomOffset={scale(12)}
      extraKeyboardSpace={scale(12)}>
      {/* Avatar */}
      <Stack align="center" marginBottom={scale(24)}>
        <Controller
          name="avatar"
          control={control}
          render={({ field: { value }, fieldState: { error } }) => {
            const fileSizeMessage = (error as { fileSize?: { message?: string } } | undefined)
              ?.fileSize?.message;
            return (
              <OnboardingAvatar
                source={value?.uri}
                onPress={isViewing ? undefined : onPickAvatar}
                hideBadge={isViewing}
                hasError={Boolean(error)}
                errorMessage={fileSizeMessage}
              />
            );
          }}
        />
      </Stack>

      {/* Name row */}
      <Stack direction="row" gap={scale(12)}>
        <Stack flex={1}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                {...sharedFieldProps}
                disabled={isViewing}
                disableStyle={disabledFieldStyle}
                label={strings.labelFirstName}
                required
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                placeholder={strings.placeholderFirstName}
                autoCapitalize="words"
                maxLength={MAX_NAME_LENGTH}
              />
            )}
          />
        </Stack>
        <Stack flex={1}>
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                {...sharedFieldProps}
                disabled={isViewing}
                disableStyle={disabledFieldStyle}
                label={strings.labelLastName}
                required
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                placeholder={strings.placeholderLastName}
                autoCapitalize="words"
                maxLength={MAX_NAME_LENGTH}
              />
            )}
          />
        </Stack>
      </Stack>

      {/* Birth date + gender */}
      <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
        <Stack flex={1} gap={scale(4)}>
          <FieldLabel required>{strings.labelBirthDate}</FieldLabel>
          <Controller
            name="dob"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <DateTimePicker
                  value={value}
                  onDateChange={onChange}
                  disabled={isViewing}
                  disableStyle={disabledFieldStyle}
                  placeholder={strings.placeholderBirthDate}
                  maximumDate={new Date()}
                  status={error?.message ? 'error' : 'default'}
                  triggerStyle={styles.fieldContent}
                  iconName="IcCalendar2"
                  iconColor={guardianColors[6]}
                  iconSize={scale(20)}
                  displayVariant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  presentation="sheet"
                  title={strings.labelBirthDate}
                />
                {error?.message ? <FieldError>{error.message}</FieldError> : null}
              </>
            )}
          />
        </Stack>
        <Stack flex={1}>
          <Controller
            name="gender"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormSelectInput
                {...selectFieldProps}
                disabled={isViewing}
                disableStyle={disabledFieldStyle}
                presentation="sheet"
                label={strings.labelGender}
                required
                options={GENDER_OPTIONS}
                value={value}
                onValueChange={next => onChange(next as PlayerGender)}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                placeholder={isEditing || isViewing ? '--' : strings.genderPlaceholder}
              />
            )}
          />
        </Stack>
      </Stack>

      {/* Height + dominant hand (edit / view only) */}
      {showHeightAndHand ? (
        <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
          <Stack flex={1}>
            <Controller
              name="height"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <FormSelectInput
                  {...selectFieldProps}
                  disabled={isViewing}
                  disableStyle={disabledFieldStyle}
                  presentation="sheet"
                  label={strings.labelHeight}
                  required={false}
                  options={HEIGHT_OPTIONS}
                  value={value}
                  onValueChange={onChange}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.placeholderHeight}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <Controller
              name="dominantHand"
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormSelectInput
                  {...selectFieldProps}
                  disabled={isViewing}
                  disableStyle={disabledFieldStyle}
                  presentation="sheet"
                  label={strings.labelDominantHand}
                  required={false}
                  options={DOMINANT_HAND_OPTIONS}
                  value={value}
                  onValueChange={next => onChange(next as DominantHand)}
                />
              )}
            />
          </Stack>
        </Stack>
      ) : null}

      {/* Address */}
      <Stack marginBottom={isViewing ? 0 : scale(12)}>
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.sectionHeader}>
          {strings.sectionAddress}
        </Text>
      </Stack>
      {/* "Same address with me" is an add-only convenience; hidden in edit / view. */}
      {showSameAddress ? (
        <Controller
          name="sameAddress"
          control={control}
          render={({ field: { value } }) => (
            <Checkbox checked={value} onValueChange={onToggleSameAddress}>
              {/* Render the label ourselves so it uses the onboarding font (Open Sans),
                    not the Checkbox component's default Inter. Matches step 1's typography. */}
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.checkboxLabel}>
                {strings.sameAddress}
              </Text>
            </Checkbox>
          )}
        />
      ) : null}

      {/* Address fields are hidden when reusing the guardian's address. */}
      {showAddressFields ? (
        <>
          {/* Country + state/province */}
          <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
            <Stack flex={1}>
              <Controller
                name="country"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <CountryRegionField
                    {...selectFieldProps}
                    disabled={isViewing}
                    disableStyle={disabledFieldStyle}
                    label={strings.labelCountry}
                    required={false}
                    value={value}
                    onValueChange={next => {
                      onChange(next);
                      // Clear the state — the previous selection may not exist in the new country.
                      setValue('state', null, { shouldDirty: true });
                    }}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                    placeholder={strings.placeholderCountry}
                  />
                )}
              />
            </Stack>
            <Stack flex={1}>
              <Controller
                name="state"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <StateProvinceField
                    {...stateFieldProps}
                    disabled={isViewing}
                    disableStyle={disabledFieldStyle}
                    label={strings.labelState}
                    required={false}
                    country={selectedCountry}
                    value={value}
                    onValueChange={onChange}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                  />
                )}
              />
            </Stack>
          </Stack>

          {/* City + zip */}
          <Stack direction="row" gap={scale(12)} marginTop={scale(12)} align="flex-start">
            <Stack flex={1}>
              <Controller
                name="city"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <FormInput
                    {...sharedFieldProps}
                    disabled={isViewing}
                    disableStyle={disabledFieldStyle}
                    label={strings.labelCity}
                    required={false}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                    placeholder={strings.placeholderCity}
                    autoCapitalize="words"
                    maxLength={MAX_CITY_LENGTH}
                  />
                )}
              />
            </Stack>
            <Stack flex={1}>
              <Controller
                name="zipCode"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <FormInput
                    {...sharedFieldProps}
                    disabled={isViewing}
                    disableStyle={disabledFieldStyle}
                    label={strings.labelZip}
                    required={false}
                    value={value}
                    onChangeText={zipCode => onChange(zipCode.replace(/[^0-9]/g, ''))}
                    onBlur={onBlur}
                    status={error?.message ? 'error' : 'default'}
                    errorMessage={error?.message}
                    placeholder={strings.placeholderZip}
                    keyboardType="numeric"
                    maxLength={MAX_ZIP_LENGTH}
                  />
                )}
              />
            </Stack>
          </Stack>
        </>
      ) : null}
      {isTablet && isEditing ? <DeletePlayerButton onPress={onDelete} /> : null}
    </KeyboardAwareScrollView>
  );
}
